import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import DashboardNav from "../components/DashboardNav";
import {
  ImagePlus,
  X,
  CalendarDays,
  DollarSign,
  Gamepad2,
  FileText,
  Hash,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  Users,
  Eye,
  MonitorPlay,
  Check,
  Link as LinkIcon,
  FolderOpen,
  Globe,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const PLATFORM_OPTIONS = [
  { id: "youtube", label: "YouTube", color: "red" },
  { id: "twitch", label: "Twitch", color: "purple" },
  { id: "tiktok", label: "TikTok", color: "pink" },
  { id: "twitter", label: "X / Twitter", color: "blue" },
  { id: "instagram", label: "Instagram", color: "orange" },
  { id: "kick", label: "Kick", color: "green" },
];

const GENRE_OPTIONS = [
  "Action", "Adventure", "RPG", "Strategy", "Simulation", "Sports",
  "Racing", "Puzzle", "Platformer", "Horror", "Survival", "Sandbox",
  "FPS", "MMORPG", "MOBA", "Battle Royale", "Roguelike", "Visual Novel",
  "Indie", "Casual",
];

const STEAM_COUNTRIES = [
  "Argentina", "Australia", "Austria", "Belgium", "Brazil", "Bulgaria",
  "Canada", "Chile", "China", "Colombia", "Costa Rica", "Croatia",
  "Czech Republic", "Denmark", "Ecuador", "Egypt", "Estonia", "Finland",
  "France", "Georgia", "Germany", "Greece", "Hong Kong", "Hungary",
  "India", "Indonesia", "Ireland", "Israel", "Italy", "Japan",
  "Kazakhstan", "Korea, Republic of", "Kuwait", "Latvia", "Lithuania",
  "Macau", "Malaysia", "Mexico", "Netherlands", "New Zealand", "Norway",
  "Pakistan", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
  "Romania", "Russia", "Saudi Arabia", "Serbia", "Singapore", "Slovakia",
  "Slovenia", "South Africa", "Spain", "Sweden", "Switzerland", "Taiwan",
  "Thailand", "Turkey", "Ukraine", "United Arab Emirates", "United Kingdom",
  "United States", "Uruguay", "Viet Nam",
];

const DEFAULT_PRICING_TIERS = [
  {
    name: "Tier 1",
    price: "",
    countries: [
      "United States", "Canada", "United Kingdom", "Germany", "France",
      "Australia", "Japan", "Korea, Republic of", "Netherlands", "Sweden",
      "Norway", "Denmark", "Switzerland", "Austria", "Finland", "Ireland",
      "New Zealand", "Belgium",
    ],
  },
  {
    name: "Tier 2",
    price: "",
    countries: [
      "Brazil", "Poland", "Turkey", "China", "India", "Mexico",
      "Argentina", "Chile", "Colombia", "Czech Republic", "Hungary",
      "Romania", "Thailand", "Philippines", "Indonesia", "Portugal",
      "Spain", "Italy", "Greece",
    ],
  },
  { name: "Tier 3", price: "", countries: [] },
];

const AUDIENCE_OPTIONS = [
  { value: 0, label: "No minimum" },
  { value: 100, label: "100+" },
  { value: 1000, label: "1,000+" },
  { value: 5000, label: "5,000+" },
  { value: 10000, label: "10,000+" },
  { value: 50000, label: "50,000+" },
  { value: 100000, label: "100,000+" },
  { value: 500000, label: "500,000+" },
  { value: 1000000, label: "1,000,000+" },
];

function EditCampaignPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const coverInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [initialLoading, setInitialLoading] = useState(true);
  const [form, setForm] = useState({
    title: "",
    gameName: "",
    description: "",
    startDate: "",
    endDate: "",
    maxBudget: "",
    pricePerWishlist: "",
    steamAppId: "",
    steamPageUrl: "",
    maxInfluencers: "",
    minAudienceSize: 0,
    youtubeTrailerUrl: "",
    mediaKitUrl: "",
  });
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [campaignType, setCampaignType] = useState("wishlist");
  const [useCountryPricing, setUseCountryPricing] = useState(false);
  const [pricingTiers, setPricingTiers] = useState(DEFAULT_PRICING_TIERS.map(t => ({ ...t, countries: [...t.countries] })));
  const [expandedTier, setExpandedTier] = useState(null);

  // Original values to enforce "only increase" rule
  const [originalBudget, setOriginalBudget] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [originalTierPrices, setOriginalTierPrices] = useState([]);

  // Existing image URLs from Firestore
  const [existingCoverUrl, setExistingCoverUrl] = useState("");
  const [existingBannerUrl, setExistingBannerUrl] = useState("");
  const [existingGalleryUrls, setExistingGalleryUrls] = useState([]);

  // New image files (if user uploads replacements)
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    async function loadCampaign() {
      try {
        const snap = await getDoc(doc(db, "campaigns", id));
        if (!snap.exists()) {
          setError("Campaign not found.");
          setInitialLoading(false);
          return;
        }
        const data = snap.data();

        if (data.publisherId !== currentUser.uid && userProfile?.role !== "admin") {
          setError("You don't have permission to edit this campaign.");
          setInitialLoading(false);
          return;
        }

        setForm({
          title: data.title || "",
          gameName: data.gameName || "",
          description: data.description || "",
          startDate: data.startDate || "",
          endDate: data.endDate || "",
          maxBudget: data.maxBudget?.toString() || "",
          pricePerWishlist: (data.campaignType === "purchase" ? data.pricePerPurchase : data.pricePerWishlist)?.toString() || "",
          steamAppId: data.steamAppId || "",
          steamPageUrl: data.steamPageUrl || "",
          maxInfluencers: data.maxInfluencers?.toString() || "",
          minAudienceSize: data.minAudienceSize || 0,
          youtubeTrailerUrl: data.youtubeTrailerUrl || "",
          mediaKitUrl: data.mediaKitUrl || "",
        });
        setSelectedPlatforms(data.platforms || []);
        setSelectedGenres(data.genres || []);
        setCampaignType(data.campaignType || "wishlist");
        setUseCountryPricing(data.useCountryPricing || false);
        if (data.pricingTiers && data.pricingTiers.length > 0) {
          setPricingTiers(data.pricingTiers.map(t => ({ ...t, price: t.price?.toString() || "", countries: [...(t.countries || [])] })));
        }

        const loadedBudget = data.maxBudget || 0;
        const loadedPrice = (data.campaignType === "purchase" ? data.pricePerPurchase : data.pricePerWishlist) || 0;
        setOriginalBudget(loadedBudget);
        setOriginalPrice(loadedPrice);
        if (data.pricingTiers && data.pricingTiers.length > 0) {
          setOriginalTierPrices(data.pricingTiers.map(t => t.price || 0));
        }

        setExistingCoverUrl(data.coverImageUrl || "");
        setExistingBannerUrl(data.bannerImageUrl || "");
        setExistingGalleryUrls(data.galleryImageUrls || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load campaign.");
      }
      setInitialLoading(false);
    }
    loadCampaign();
  }, [id, currentUser.uid]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function validateImage(file) {
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB.");
      return false;
    }
    return true;
  }

  function handleCoverSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateImage(file)) return;
    setCoverImage(file);
    setExistingCoverUrl("");
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);
    setError("");
  }

  function removeCoverImage() {
    setCoverImage(null);
    setCoverPreview(null);
    setExistingCoverUrl("");
    if (coverInputRef.current) coverInputRef.current.value = "";
  }

  function handleBannerSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateImage(file)) return;
    setBannerImage(file);
    setExistingBannerUrl("");
    const reader = new FileReader();
    reader.onloadend = () => setBannerPreview(reader.result);
    reader.readAsDataURL(file);
    setError("");
  }

  function removeBannerImage() {
    setBannerImage(null);
    setBannerPreview(null);
    setExistingBannerUrl("");
    if (bannerInputRef.current) bannerInputRef.current.value = "";
  }

  function handleGallerySelect(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const valid = files.filter((f) => validateImage(f));
    if (existingGalleryUrls.length + galleryImages.length + valid.length > 10) {
      setError("Maximum 10 gallery images allowed.");
      return;
    }
    setGalleryImages((prev) => [...prev, ...valid]);
    valid.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        setGalleryPreviews((prev) => [...prev, reader.result]);
      reader.readAsDataURL(file);
    });
    if (galleryInputRef.current) galleryInputRef.current.value = "";
    setError("");
  }

  function removeGalleryImage(index) {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
  }

  function removeExistingGalleryImage(index) {
    setExistingGalleryUrls((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!form.title.trim()) return setError("Title is required.");
    if (!form.gameName.trim()) return setError("Game name is required.");
    if (!form.description.trim()) return setError("Description is required.");
    if (!form.startDate) return setError("Start date is required.");
    if (!form.endDate) return setError("End date is required.");
    if (new Date(form.endDate) <= new Date(form.startDate))
      return setError("End date must be after start date.");
    if (!form.maxBudget || parseFloat(form.maxBudget) <= 0)
      return setError("Max budget must be greater than 0.");
    if (parseFloat(form.maxBudget) < originalBudget)
      return setError(`Budget cannot be lower than the original $${originalBudget.toLocaleString()}.`);
    if (!form.pricePerWishlist || parseFloat(form.pricePerWishlist) <= 0)
      return setError(`Price per ${campaignType} must be greater than 0.`);
    if (parseFloat(form.pricePerWishlist) < originalPrice)
      return setError(`Price per ${campaignType} cannot be lower than the original $${originalPrice}.`);
    if (!form.steamAppId.trim()) return setError("Steam App ID is required.");
    if (!existingCoverUrl && !coverImage) return setError("Please upload a cover image.");
    if (form.maxInfluencers && parseInt(form.maxInfluencers) < 1)
      return setError("Max influencers must be at least 1.");
    if (selectedPlatforms.length === 0)
      return setError("Please select at least one platform.");
    if (useCountryPricing) {
      for (let i = 0; i < pricingTiers.length; i++) {
        const tier = pricingTiers[i];
        if (!tier.price || parseFloat(tier.price) <= 0) {
          return setError(`Please set a price for ${tier.name}.`);
        }
        if (originalTierPrices[i] && parseFloat(tier.price) < originalTierPrices[i]) {
          return setError(`${tier.name} price cannot be lower than the original $${originalTierPrices[i]}.`);
        }
      }
    }

    setLoading(true);

    try {
      // Upload new cover if changed
      let coverImageUrl = existingCoverUrl;
      if (coverImage) {
        const imageRef = ref(
          storage,
          `campaign-covers/${currentUser.uid}/${Date.now()}_${coverImage.name}`
        );
        const snapshot = await uploadBytes(imageRef, coverImage);
        coverImageUrl = await getDownloadURL(snapshot.ref);
      }

      // Upload new banner if changed
      let bannerImageUrl = existingBannerUrl;
      if (bannerImage) {
        const bannerRef = ref(
          storage,
          `campaign-banners/${currentUser.uid}/${Date.now()}_${bannerImage.name}`
        );
        const snapshot = await uploadBytes(bannerRef, bannerImage);
        bannerImageUrl = await getDownloadURL(snapshot.ref);
      }

      // Upload new gallery images
      const newGalleryUrls = [];
      for (const file of galleryImages) {
        const galleryRef = ref(
          storage,
          `campaign-gallery/${currentUser.uid}/${Date.now()}_${file.name}`
        );
        const snapshot = await uploadBytes(galleryRef, file);
        const url = await getDownloadURL(snapshot.ref);
        newGalleryUrls.push(url);
      }
      const galleryImageUrls = [...existingGalleryUrls, ...newGalleryUrls];

      await updateDoc(doc(db, "campaigns", id), {
        title: form.title.trim(),
        gameName: form.gameName.trim(),
        description: form.description.trim(),
        startDate: form.startDate,
        endDate: form.endDate,
        maxBudget: parseFloat(form.maxBudget),
        campaignType,
        pricePerWishlist: campaignType === "wishlist" ? parseFloat(form.pricePerWishlist) : 0,
        pricePerPurchase: campaignType === "purchase" ? parseFloat(form.pricePerWishlist) : 0,
        steamAppId: form.steamAppId.trim(),
        steamPageUrl: form.steamPageUrl.trim() || `https://store.steampowered.com/app/${form.steamAppId.trim()}`,
        maxInfluencers: form.maxInfluencers ? parseInt(form.maxInfluencers) : null,
        minAudienceSize: form.minAudienceSize,
        platforms: selectedPlatforms,
        genres: selectedGenres,
        coverImageUrl,
        bannerImageUrl,
        galleryImageUrls,
        youtubeTrailerUrl: form.youtubeTrailerUrl.trim() || "",
        mediaKitUrl: form.mediaKitUrl.trim() || "",
        useCountryPricing,
        pricingTiers: useCountryPricing
          ? pricingTiers.map((t) => ({ name: t.name, price: parseFloat(t.price), countries: t.countries }))
          : [],
      });

      setSuccess(true);
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err) {
      console.error(err);
      setError("Failed to update campaign. Please try again.");
    }
    setLoading(false);
  }

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-3">Campaign Updated!</h2>
          <p className="text-gray-400 text-lg">
            Your changes have been saved. Redirecting to dashboard...
          </p>
        </div>
      </div>
    );
  }

  const currentCoverPreview = coverPreview || existingCoverUrl;
  const currentBannerPreview = bannerPreview || existingBannerUrl;
  const totalGalleryCount = existingGalleryUrls.length + galleryImages.length;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Edit Campaign</h1>
          <p className="text-gray-400">
            Update your campaign details. Changes will be applied immediately.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Cover Photo (Square) */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Cover Photo
            </label>
            <p className="text-xs text-gray-600 mb-2">
              Square image used on campaign cards. Recommended 1:1 ratio.
            </p>
            {currentCoverPreview ? (
              <div className="relative group w-48 h-48 rounded-2xl overflow-hidden border border-gray-800">
                <img
                  src={currentCoverPreview}
                  alt="Cover preview"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => coverInputRef.current?.click()}
                    className="bg-brand-500/80 hover:bg-brand-500 text-white p-2 rounded-full transition-colors"
                  >
                    <ImagePlus className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={removeCoverImage}
                    className="bg-red-500/80 hover:bg-red-500 text-white p-2 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                className="w-48 h-48 border-2 border-dashed border-gray-800 hover:border-gray-700 rounded-2xl flex flex-col items-center justify-center gap-2 transition-colors bg-gray-900/30 hover:bg-gray-900/50"
              >
                <ImagePlus className="w-8 h-8 text-gray-600" />
                <p className="text-xs text-gray-500">Upload cover</p>
              </button>
            )}
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={handleCoverSelect}
              className="hidden"
            />
          </div>

          {/* Banner Photo */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Banner Photo <span className="text-gray-600 font-normal">(optional)</span>
            </label>
            <p className="text-xs text-gray-600 mb-2">
              Wide image displayed at the top of the campaign page. Recommended 16:9 or wider.
            </p>
            {currentBannerPreview ? (
              <div className="relative group rounded-2xl overflow-hidden border border-gray-800">
                <img
                  src={currentBannerPreview}
                  alt="Banner preview"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => bannerInputRef.current?.click()}
                    className="bg-brand-500/80 hover:bg-brand-500 text-white p-2 rounded-full transition-colors"
                  >
                    <ImagePlus className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={removeBannerImage}
                    className="bg-red-500/80 hover:bg-red-500 text-white p-2 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => bannerInputRef.current?.click()}
                className="w-full h-40 border-2 border-dashed border-gray-800 hover:border-gray-700 rounded-2xl flex flex-col items-center justify-center gap-3 transition-colors bg-gray-900/30 hover:bg-gray-900/50"
              >
                <ImagePlus className="w-8 h-8 text-gray-600" />
                <div className="text-center">
                  <p className="text-sm text-gray-400">Click to upload banner</p>
                  <p className="text-xs text-gray-600 mt-1">PNG, JPG up to 5MB</p>
                </div>
              </button>
            )}
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              onChange={handleBannerSelect}
              className="hidden"
            />
          </div>

          {/* Gallery Photos */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Gallery Photos <span className="text-gray-600 font-normal">(optional)</span>
            </label>
            <p className="text-xs text-gray-600 mb-2">
              Additional screenshots or artwork. Up to 10 images, shown as a carousel on the campaign page.
            </p>
            {(existingGalleryUrls.length > 0 || galleryPreviews.length > 0) && (
              <div className="flex flex-wrap gap-3 mb-3">
                {existingGalleryUrls.map((src, i) => (
                  <div
                    key={`existing-${i}`}
                    className="relative group w-28 h-28 rounded-xl overflow-hidden border border-gray-800"
                  >
                    <img
                      src={src}
                      alt={`Gallery ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeExistingGalleryImage(i)}
                        className="bg-red-500/80 hover:bg-red-500 text-white p-1.5 rounded-full transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {galleryPreviews.map((src, i) => (
                  <div
                    key={`new-${i}`}
                    className="relative group w-28 h-28 rounded-xl overflow-hidden border border-brand-500/30"
                  >
                    <img
                      src={src}
                      alt={`New gallery ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(i)}
                        className="bg-red-500/80 hover:bg-red-500 text-white p-1.5 rounded-full transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {totalGalleryCount < 10 && (
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className="w-28 h-28 border-2 border-dashed border-gray-800 hover:border-gray-700 rounded-xl flex flex-col items-center justify-center gap-1 transition-colors bg-gray-900/30 hover:bg-gray-900/50"
              >
                <ImagePlus className="w-6 h-6 text-gray-600" />
                <p className="text-xs text-gray-500">Add</p>
              </button>
            )}
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleGallerySelect}
              className="hidden"
            />
          </div>

          {/* YouTube Trailer */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              YouTube Trailer <span className="text-gray-600 font-normal">(optional)</span>
            </label>
            <div className="relative">
              <MonitorPlay className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="url"
                name="youtubeTrailerUrl"
                value={form.youtubeTrailerUrl}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
            </div>
          </div>

          {/* Media Kit */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Media Kit <span className="text-gray-600 font-normal">(optional)</span>
            </label>
            <div className="relative">
              <FolderOpen className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="url"
                name="mediaKitUrl"
                value={form.mediaKitUrl}
                onChange={handleChange}
                placeholder="https://drive.google.com/..."
                className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Campaign Title
            </label>
            <div className="relative">
              <FileText className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g. Starfield Explorer Launch Campaign"
                className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
            </div>
          </div>

          {/* Game Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Game Name
            </label>
            <div className="relative">
              <Gamepad2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                name="gameName"
                value={form.gameName}
                onChange={handleChange}
                placeholder="e.g. Starfield"
                className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Description
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              placeholder="Describe your game and what kind of influencers you're looking for..."
              className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all resize-none"
            />
          </div>

          {/* Genre */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Game Genre
            </label>
            <div className="flex flex-wrap gap-2">
              {GENRE_OPTIONS.map((genre) => {
                const isSelected = selectedGenres.includes(genre);
                return (
                  <button
                    key={genre}
                    type="button"
                    onClick={() =>
                      setSelectedGenres((prev) =>
                        isSelected
                          ? prev.filter((g) => g !== genre)
                          : [...prev, genre]
                      )
                    }
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                      isSelected
                        ? "bg-brand-500/10 border-brand-500/30 text-brand-400"
                        : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700 hover:text-gray-300"
                    }`}
                  >
                    {genre}
                    {isSelected && <span className="ml-1">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>


          {/* Platforms */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Campaign Platforms
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PLATFORM_OPTIONS.map((platform) => {
                const isSelected = selectedPlatforms.includes(platform.id);
                return (
                  <button
                    key={platform.id}
                    type="button"
                    onClick={() =>
                      setSelectedPlatforms((prev) =>
                        isSelected
                          ? prev.filter((p) => p !== platform.id)
                          : [...prev, platform.id]
                      )
                    }
                    className={`relative flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                      isSelected
                        ? "bg-brand-500/10 border-brand-500/30 text-white"
                        : "bg-gray-900/50 border-gray-800 text-gray-400 hover:border-gray-700 hover:text-gray-300"
                    }`}
                  >
                    <MonitorPlay className="w-4 h-4" />
                    {platform.label}
                    {isSelected && (
                      <Check className="w-4 h-4 text-brand-400 absolute right-3" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Max Influencers & Min Audience */}
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Max Influencers
              </label>
              <div className="relative">
                <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="number"
                  name="maxInfluencers"
                  value={form.maxInfluencers}
                  onChange={handleChange}
                  placeholder="Unlimited"
                  min="1"
                  step="1"
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                />
              </div>
              <p className="text-xs text-gray-600 mt-1.5">
                Leave empty for unlimited influencers
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Min Audience Size
              </label>
              <div className="relative">
                <Eye className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 pointer-events-none" />
                <select
                  name="minAudienceSize"
                  value={form.minAudienceSize}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      minAudienceSize: parseInt(e.target.value),
                    }))
                  }
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white appearance-none focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all cursor-pointer"
                >
                  {AUDIENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>



          {/* Budget & Price */}
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Max Spending Budget ($)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="number"
                  name="maxBudget"
                  value={form.maxBudget}
                  onChange={handleChange}
                  placeholder="10000"
                  min={originalBudget || 1}
                  step="0.01"
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                />
              </div>
              <p className="text-xs text-gray-600 mt-1.5">
                Cannot be lower than ${originalBudget.toLocaleString()}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                {campaignType === "wishlist" ? "Price Per Wishlist ($)" : "Price Per Purchase ($)"}
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="number"
                  name="pricePerWishlist"
                  value={form.pricePerWishlist}
                  onChange={handleChange}
                  placeholder={campaignType === "wishlist" ? "1.00" : "5.00"}
                  min={originalPrice || 0.01}
                  step="0.01"
                  className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-3 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
                />
              </div>
              <p className="text-xs text-gray-600 mt-1.5">
                Cannot be lower than ${originalPrice}
              </p>
            </div>
          </div>

          {/* Country-Based Pricing */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-300">
                  Country-Based Pricing
                </label>
                <p className="text-xs text-gray-600 mt-0.5">
                  Set different prices per {campaignType} based on the player's country
                </p>
              </div>
              <button
                type="button"
                disabled
                className={`relative w-11 h-6 rounded-full cursor-not-allowed opacity-60 ${
                  useCountryPricing ? "bg-brand-500" : "bg-gray-700"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    useCountryPricing ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>

            {useCountryPricing && (
              <div className="space-y-3 mt-4">
                {pricingTiers.map((tier, tierIdx) => {
                  const isLast = tier.countries.length === 0;
                  const assignedCountries = pricingTiers
                    .filter((_, i) => i !== tierIdx)
                    .flatMap((t) => t.countries);
                  const availableCountries = STEAM_COUNTRIES.filter(
                    (c) => !assignedCountries.includes(c)
                  );
                  const isExpanded = expandedTier === tierIdx;

                  return (
                    <div
                      key={tierIdx}
                      className="bg-gray-900/50 border border-gray-800 rounded-xl p-4"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex-1 flex items-center gap-3">
                          <span className="text-sm font-medium text-gray-300 w-28">{tier.name}</span>
                          <div className="relative flex-1 max-w-[160px]">
                            <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <input
                              type="number"
                              value={tier.price}
                              onChange={(e) => {
                                const updated = [...pricingTiers];
                                updated[tierIdx] = { ...updated[tierIdx], price: e.target.value };
                                setPricingTiers(updated);
                              }}
                              placeholder="0.00"
                              min={originalTierPrices[tierIdx] || 0.01}
                              step="0.01"
                              className="w-full bg-gray-800/50 border border-gray-700 rounded-lg py-1.5 pl-8 pr-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50"
                            />
                          </div>
                          <span className="text-xs text-gray-500">
                            per {campaignType}
                          </span>
                        </div>
                      </div>

                      {isLast ? (
                        <p className="text-xs text-gray-500 flex items-center gap-1.5">
                          <Globe className="w-3.5 h-3.5" />
                          Default tier — applies to all countries not in other tiers
                        </p>
                      ) : (
                        <>
                          <p className="text-xs text-gray-500 mb-2">
                            {tier.countries.length} countries
                          </p>
                          {tier.countries.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {tier.countries.map((c) => (
                                <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">
                                  {c}
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Summary Card */}
          {form.maxBudget && form.pricePerWishlist && parseFloat(form.pricePerWishlist) > 0 && (
            <div className="bg-brand-500/5 border border-brand-500/20 rounded-2xl p-6">
              <h3 className="text-sm font-medium text-brand-300 mb-3">Campaign Summary</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Budget</p>
                  <p className="text-lg font-semibold">${parseFloat(form.maxBudget).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">
                    {campaignType === "wishlist" ? "Price/Wishlist" : "Price/Purchase"}
                  </p>
                  <p className="text-lg font-semibold">${parseFloat(form.pricePerWishlist).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">
                    {campaignType === "wishlist" ? "Max Wishlists" : "Max Purchases"}
                  </p>
                  <p className="text-lg font-semibold text-green-400">
                    {Math.floor(parseFloat(form.maxBudget) / parseFloat(form.pricePerWishlist)).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 mb-6">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center gap-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-8 py-3 rounded-xl transition-all font-semibold shadow-lg shadow-brand-600/25 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="text-gray-500 hover:text-gray-300 px-6 py-3 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditCampaignPage;
